import { useState } from "react";
import { createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  getInvoicesUserService,
  payInvoiceUserService,
} from "./../../../services/userServices";
import {
  createInvoiceAdminService,
  deleteInvoiceAdminService,
  editInvoiceAdminService,
  getUsersAdminService,
} from "./../../../services/adminServices";
import {
  deliverySuccessMessage,
  modalErrorMessage,
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { setUserAction } from "../../../redux/actions/userActions/userActions";

export const invoiceUserContext = createContext({
  invoices: [],
  setInvoices: () => {},
  invoice: {},
  setInvoice: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  type: "",
  setType: () => {},
  status: "",
  setStatus: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleGetInvoices: () => {},
  handleCreateInvoice: () => {},
  handleEditInvoice: () => {},
  handleDeleteInvoice: () => {},
  handlePayInvoice: () => {},
});

const InvoiceUserContext = ({ children }) => {
  const dispatch = useDispatch();

  const { user } = useParams();

  const [invoices, setInvoices] = useState([]);
  const [invoice, setInvoice] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setInvoice({ ...val, isEdit: true });
      setValue("amount", val.amount);
      setValue("status", val.status);
      setValue("description", val.description);
      setValue("expiredAt", val.expiredAt);
      setValue("documents", val.documents);
      setValue("user", {
        value: val.user?._id,
        label: `${val.user?.firstName} ${val.user?.lastName} - ${val.user?.phoneNumber}`,
      });
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setInvoice({});
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${item.phoneNumber}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Invoices
  const handleGetInvoices = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(type && { type }),
      ...(status && { status }),
      ...(user && { user }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getInvoicesUserService(objData);
      if (status === 200) {
        setInvoices(data.result?.invoices);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Invoice
  const handleCreateInvoice = async (invoiceData) => {
    const objData = {
      ...invoiceData,
      user: invoiceData?.user?.value,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createInvoiceAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetInvoices();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Invoice
  const handleEditInvoice = async (invoiceData) => {
    const objData = {
      id: invoice._id,
      ...invoiceData,
      user: invoiceData?.user?.value,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editInvoiceAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetInvoices();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Invoice
  const handleDeleteInvoice = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteInvoiceAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetInvoices();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Pay Invoice
  const handlePayInvoice = async (gateway) => {
    const objData = {
      id: invoice._id,
      gateway,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await payInvoiceUserService(objData);
      if (status === 200) {
        const result = data?.result;
        if (!isEmpty(result?.payment)) {
          deliverySuccessMessage(data.resultMessage);
          window.location.href = result?.payment?.redirectUrl;
        } else {
          dispatch(setUserAction());
          deliverySuccessMessage(data.resultMessage, false, "5000");
        }

        handleGetInvoices();
      }
    } catch ({ response }) {
      if (response && response.status)
        modalErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <invoiceUserContext.Provider
      value={{
        invoices,
        setInvoices,
        invoice,
        setInvoice,
        filter,
        setFilter,
        sort,
        setSort,
        type,
        setType,
        status,
        setStatus,
        start,
        setStart,
        end,
        setEnd,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleGetInvoices,
        handleCreateInvoice,
        handleEditInvoice,
        handleDeleteInvoice,
        handlePayInvoice,
      }}
    >
      {children}
    </invoiceUserContext.Provider>
  );
};

export default InvoiceUserContext;
